import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  TablePagination,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../services/api';
import { useState, useEffect, useCallback, useMemo } from 'react';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AssignmentIcon from '@mui/icons-material/Assignment';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AppLayout from '../components/AppLayout';
import { formatReviewStatus, getReviewStatusColor } from '../utils/reviewStatus';
import { getApiErrorMessage } from '../utils/apiError';
import { couldNotLoad } from '../utils/userFacingCopy';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

interface Review {
  id: string;
  reviewCycleId: string;
  reviewCycleName?: string;
  employeeId: string;
  employeeName?: string;
  managerId?: string;
  managerName?: string;
  status: string | number;
  objectiveAverage?: number;
  employeeSelfRatingAverage?: number;
  behaviouralAverage?: number;
  combinedRating?: number;
  finalRating?: number | string;
}

const ratingLabels: Record<number, string> = {
  1: 'Development Required',
  2: 'Meets Expectation',
  3: 'Exceeds Expectation',
};

function normaliseRating(value: number | string | undefined): number | undefined {
  if (value == null) return undefined;
  if (typeof value === 'number' && (value === 1 || value === 2 || value === 3)) return value;
  if (typeof value === 'string') {
    const map: Record<string, number> = {
      DevelopmentRequired: 1,
      MeetsExpectation: 2,
      ExceedsExpectation: 3,
    };
    return map[value] ?? undefined;
  }
  return undefined;
}

function isFinalisedStatus(status: string | number): boolean {
  const s = String(status).toLowerCase();
  return s === 'finalised' || s === '6';
}

export default function MyReviewsPage() {
  const navigate = useNavigate();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [filteredReviews, setFilteredReviews] = useState<Review[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [trendFinalisedOnly, setTrendFinalisedOnly] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const loadReviews = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.get<Review[]>('/reviews');
      setReviews(response.data);
      setFilteredReviews(response.data);
    } catch (err: unknown) {
            setError(getApiErrorMessage(err, couldNotLoad('your reviews')));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReviews();
  }, [loadReviews]);

  useEffect(() => {
    let list = [...reviews];
    if (statusFilter !== 'all') {
      list = list.filter((r) => {
        const s = String(r.status).toLowerCase();
        return (
          (statusFilter === 'finalised' && (s === 'finalised' || r.status === 6)) ||
          (statusFilter === 'pending' && s !== 'finalised' && r.status !== 6)
        );
      });
    }
    // Same order as elsewhere: cycle 1 then cycle 2 (by cycle name e.g. FY 2026 Q1 before Q2)
    list.sort((a, b) => (a.reviewCycleName || '').localeCompare(b.reviewCycleName || ''));
    setFilteredReviews(list);
  }, [reviews, statusFilter]);

  const paginatedReviews = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredReviews.slice(start, start + rowsPerPage);
  }, [filteredReviews, page, rowsPerPage]);

  const trendData = useMemo(() => {
    const source = trendFinalisedOnly ? reviews.filter((r) => isFinalisedStatus(r.status)) : reviews;
    return [...source]
      .sort((a, b) => (a.reviewCycleName || '').localeCompare(b.reviewCycleName || ''))
      .map((review) => ({
        cycle: review.reviewCycleName || 'Unknown',
        combined: review.combinedRating ?? null,
        objective: review.objectiveAverage ?? null,
        self: review.employeeSelfRatingAverage ?? null,
      }));
  }, [reviews, trendFinalisedOnly]);

  useEffect(() => {
    setPage(0);
  }, [statusFilter]);

  return (
    <AppLayout>
      <Box sx={{ maxWidth: '1400px', mx: 'auto', pb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <IconButton onClick={() => navigate(-1)} sx={{ ml: -1 }} title="Back">
              <ArrowBackIcon />
            </IconButton>
            <AssignmentIcon sx={{ fontSize: 32, color: 'primary.main', alignSelf: 'flex-start', mt: 0.5 }} />
            <Typography variant="h4" component="h1" fontWeight="bold" sx={{ mb: 0 }}>
              Reviews
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            {filteredReviews.length} review{filteredReviews.length !== 1 ? 's' : ''}
          </Typography>
        </Box>

        <Alert severity="info" sx={{ mb: 3 }}>
          Your performance reviews across all cycles. Click a row to view details and ratings.
        </Alert>

        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
              <Box>
                <Typography variant="h6" sx={{ mb: 0.5 }}>My Rating Trend</Typography>
                <Typography variant="body2" color="text.secondary">
                  Combined, objective average, and self average over review cycles.
                </Typography>
              </Box>
              <FormControlLabel
                control={<Switch checked={trendFinalisedOnly} onChange={(e) => setTrendFinalisedOnly(e.target.checked)} />}
                label="Finalised only"
              />
            </Box>
            {trendData.length > 0 ? (
              <Box sx={{ width: '100%', height: 280 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trendData} margin={{ top: 10, right: 20, left: 0, bottom: 10 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="cycle" />
                    <YAxis domain={[1, 3]} tickCount={5} />
                    <Tooltip />
                    <Legend />
                    <Line type="monotone" dataKey="combined" name="Combined" stroke="#1976d2" strokeWidth={2} connectNulls />
                    <Line type="monotone" dataKey="objective" name="Objective Avg" stroke="#2e7d32" strokeWidth={2} connectNulls />
                    <Line type="monotone" dataKey="self" name="Self Avg" stroke="#ed6c02" strokeWidth={2} connectNulls />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No review data available for the selected trend filter.
              </Typography>
            )}
          </CardContent>
        </Card>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <FormControl sx={{ minWidth: 180 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="pending">In progress</MenuItem>
                  <MenuItem value="finalised">Finalised</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </CardContent>
        </Card>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Card sx={{ boxShadow: '0 4px 20px rgba(0,0,0,0.08)', borderRadius: 2 }}>
            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
              <TableContainer sx={{ borderTop: 0 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Review Cycle</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell align="center"><strong>Self Rating</strong></TableCell>
                      <TableCell align="center"><strong>Objective Avg</strong></TableCell>
                      <TableCell align="center"><strong>Overall Rating</strong></TableCell>
                      <TableCell><strong>Final Rating</strong></TableCell>
                      <TableCell align="right"><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedReviews.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4, border: 'none' }}>
                          <Box sx={{ textAlign: 'center', py: 2 }}>
                            <AssignmentIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                              {statusFilter !== 'all'
                                ? 'No reviews match your search or filters.'
                                : 'You have no reviews yet.'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {statusFilter !== 'all' ? 'Try adjusting your filter.' : 'Reviews will appear here once cycles are created and assigned.'}
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedReviews.map((review) => (
                        <TableRow
                          key={review.id}
                          hover
                          sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                          onClick={() => navigate(`/reviews/${review.id}`)}
                        >
                          <TableCell>
                            <Typography variant="body2" fontWeight={500}>
                              {review.reviewCycleName || 'Unknown Cycle'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
label={formatReviewStatus(review.status)}
                            size="small"
                            color={getReviewStatusColor(review.status)}
                            />
                          </TableCell>
                          <TableCell align="center">
                            {review.employeeSelfRatingAverage != null ? (
                              <Typography variant="body2" fontWeight={500} color="warning.dark">
                                {review.employeeSelfRatingAverage.toFixed(1)}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary">—</Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {review.objectiveAverage != null ? (
                              <Typography variant="body2" fontWeight={500}>
                                {review.objectiveAverage.toFixed(1)}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary">—</Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {review.combinedRating != null ? (
                              <Typography variant="body2" fontWeight={500} color="primary.main">
                                {review.combinedRating.toFixed(1)}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary">—</Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            {normaliseRating(review.finalRating) != null ? (
                              <Typography variant="body2">
                                {ratingLabels[normaliseRating(review.finalRating)!]}
                              </Typography>
                            ) : (
                              <Typography variant="body2" color="text.secondary">—</Typography>
                            )}
                          </TableCell>
                          <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                            <Button
                              size="small"
                              variant="outlined"
                              color="primary"
                              startIcon={<VisibilityIcon />}
                              onClick={() => navigate(`/reviews/${review.id}`)}
                              sx={{ textTransform: 'none' }}
                            >
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={filteredReviews.length}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                sx={{ borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}
              />
            </CardContent>
          </Card>
        )}
      </Box>
    </AppLayout>
  );
}
